import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { VeteranVerifyDialog } from "../components/VeteranVerifyDialog";
import {
  money, ROOF_LABELS, FREQ_LABELS, SLOT_LABELS, ADDON_LABELS,
} from "../lib/pricing";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Calendar, Plus, MapPin, CreditCard, ShieldCheck, Loader2, Sun,
  CalendarClock, XCircle, Droplets, Sparkles, FileDown, RefreshCw,
} from "lucide-react";

const STATUS_STYLE = {
  scheduled: "bg-blue-100 text-blue-700",
  en_route: "bg-amber-100 text-amber-700",
  in_progress: "bg-purple-100 text-purple-700",
  completed: "bg-emerald-100 text-emerald-700",
  paid: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-200 text-slate-500",
};
const STATUS_LABEL = {
  scheduled: "Scheduled", en_route: "En Route", in_progress: "In Progress",
  completed: "Completed", paid: "Paid", cancelled: "Cancelled",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookings, setBookings] = useState(null);
  const [reschedule, setReschedule] = useState(null);
  const [rsDate, setRsDate] = useState("");
  const [rsSlot, setRsSlot] = useState("morning");
  const [busy, setBusy] = useState(false);
  const [showVerify, setShowVerify] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBookings([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const payNow = async (b) => {
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: b.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const doReschedule = async () => {
    if (!rsDate) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      await api.put(`/bookings/${reschedule.id}/reschedule`, { service_date: rsDate, time_slot: rsSlot });
      toast.success("Booking rescheduled successfully!");
      setReschedule(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doCancel = async (b) => {
    try {
      await api.put(`/bookings/${b.id}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadReceipt = async (b) => {
    try {
      const res = await api.get(`/bookings/${b.id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `valorclean-receipt-${b.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download receipt");
    }
  };

  const active = (bookings || []).filter((b) => !["cancelled", "completed"].includes(b.status));
  const history = (bookings || []).filter((b) => ["cancelled", "completed", "paid"].includes(b.status));
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <VeteranVerifyDialog open={showVerify} setOpen={setShowVerify} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-black text-slate-900">Hi, {user?.name?.split(" ")[0]} 👋</h1>
            <p className="text-slate-500 mt-1 flex items-center gap-2">
              {user?.is_veteran && <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><ShieldCheck className="w-4 h-4" /> Veteran</span>}
              Manage your solar cleanings below.
            </p>
          </div>
          <Button onClick={() => navigate("/book")} data-testid="dashboard-new-booking" className="rounded-full bg-emerald-700 hover:bg-emerald-800 gap-2"><Plus className="w-4 h-4" /> New Booking</Button>
        </div>

        {/* guarantee strip */}
        <div className="mt-6 flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-2 text-sm font-semibold bg-white border border-emerald-200 text-emerald-800 px-4 py-2 rounded-full"><Droplets className="w-4 h-4" /> Pure-water streak-free guarantee</span>
          <span className="inline-flex items-center gap-2 text-sm font-semibold bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-full"><Sparkles className="w-4 h-4 text-amber-500" /> Free re-clean if you're not thrilled</span>
        </div>

        {user?.is_veteran && !user?.veteran_verified && (
          <div className="mt-6 bg-slate-900 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid="verify-banner">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-amber-400" />
              <div>
                <div className="text-white font-bold">Verify your veteran status</div>
                <div className="text-slate-400 text-sm">Unlock 15% off your first cleaning.</div>
              </div>
            </div>
            <Button onClick={() => setShowVerify(true)} data-testid="open-verify-btn" className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">Verify now</Button>
          </div>
        )}

        {bookings === null ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-emerald-700" /></div>
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-14 text-center mt-8" data-testid="dashboard-empty">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto"><Sun className="w-7 h-7 text-emerald-700" /></div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mt-5">No bookings yet</h3>
            <p className="text-slate-500 mt-2">Book your first cleaning and start reclaiming lost solar output.</p>
            <Button onClick={() => navigate("/book")} className="mt-6 rounded-full bg-emerald-700 hover:bg-emerald-800">Book a cleaning</Button>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-lg font-bold text-slate-900 mt-10 mb-4">Active bookings</h2>
            {active.length === 0 ? <p className="text-slate-400 text-sm mb-6">No active bookings.</p> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {active.map((b) => (
                  <BookingCard key={b.id} b={b} today={today} onPay={payNow} onReceipt={downloadReceipt}
                    onReschedule={() => { setReschedule(b); setRsDate(b.service_date); setRsSlot(b.time_slot); }}
                    onCancel={doCancel} />
                ))}
              </div>
            )}

            {history.length > 0 && (
              <>
                <h2 className="font-heading text-lg font-bold text-slate-900 mt-12 mb-4">History</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {history.map((b) => <BookingCard key={b.id} b={b} today={today} onReceipt={downloadReceipt} readOnly />)}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <Dialog open={!!reschedule} onOpenChange={(o) => !o && setReschedule(null)}>
        <DialogContent data-testid="reschedule-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reschedule cleaning</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-sm font-semibold">New date</label><Input type="date" min={today} value={rsDate} onChange={(e) => setRsDate(e.target.value)} data-testid="reschedule-date" className="mt-1.5 h-11" /></div>
            <div>
              <label className="text-sm font-semibold block mb-2">Arrival window</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(SLOT_LABELS).map((s) => (
                  <button key={s} onClick={() => setRsSlot(s)} data-testid={`reschedule-slot-${s}`}
                    className={`px-3 py-2.5 rounded-xl text-sm font-semibold border ${rsSlot === s ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200"}`}>{SLOT_LABELS[s]}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReschedule(null)}>Cancel</Button>
            <Button onClick={doReschedule} disabled={busy} data-testid="reschedule-confirm" className="bg-emerald-700 hover:bg-emerald-800 gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingCard({ b, today, onPay, onReschedule, onCancel, onReceipt, readOnly }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid={`booking-card-${b.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-heading font-bold text-slate-900 text-lg">{b.panels} panels · {ROOF_LABELS[b.roof_type]}</div>
          <div className="text-sm text-slate-500">{FREQ_LABELS[b.frequency]}</div>
          {b.auto_scheduled && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full mt-1.5" data-testid={`auto-scheduled-${b.id}`}>
              <RefreshCw className="w-3 h-3" /> Auto-scheduled visit
            </span>
          )}
        </div>
        <Badge className={`${STATUS_STYLE[b.status]} border-0 font-semibold`}>{STATUS_LABEL[b.status]}</Badge>
      </div>

      <div className="space-y-2 mt-4 text-sm text-slate-600">
        <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" /> {b.service_date} · {SLOT_LABELS[b.time_slot]}</div>
        <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> {b.address}, {b.city}</div>
        {b.addons?.length > 0 && <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-slate-400" /> {b.addons.map((a) => ADDON_LABELS[a]).join(", ")}</div>}
        {b.technician && <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Tech: {b.technician}</div>}
      </div>

      <div className="flex items-end justify-between border-t border-slate-100 mt-4 pt-4">
        <div>
          {b.veteran_discount > 0 && <div className="text-xs text-amber-600 font-semibold">Vet discount −{money(b.veteran_discount)}</div>}
          <div className="text-2xl font-black font-mono text-slate-900">{money(b.total)}</div>
        </div>
        <Badge variant="outline" className={b.payment_status === "paid" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>
          {b.payment_status === "paid" ? "Paid" : "Unpaid"}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {b.payment_status === "paid" && (
          <Button size="sm" variant="outline" onClick={() => onReceipt(b)} data-testid={`receipt-btn-${b.id}`} className="rounded-full gap-1.5"><FileDown className="w-4 h-4" /> Receipt</Button>
        )}
        {!readOnly && b.payment_status !== "paid" && (
          <Button size="sm" onClick={() => onPay(b)} data-testid={`pay-btn-${b.id}`} className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold gap-1.5"><CreditCard className="w-4 h-4" /> Pay now</Button>
        )}
        {!readOnly && (
          <>
            <Button size="sm" variant="outline" onClick={onReschedule} data-testid={`reschedule-btn-${b.id}`} className="rounded-full gap-1.5"><CalendarClock className="w-4 h-4" /> Reschedule</Button>
            <Button size="sm" variant="ghost" onClick={() => onCancel(b)} data-testid={`cancel-btn-${b.id}`} className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5"><XCircle className="w-4 h-4" /> Cancel</Button>
          </>
        )}
      </div>
    </div>
  );
}
